// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState } from 'react';
import {
  ExtensionUrl,
  type AnswerOption,
  type Extension,
  type QuestionnaireItem,
} from '@intakeform/fhir';
import { SUPPORTED_LANGUAGES, type Language } from '@intakeform/ui';
import { translateBatch } from '../../api/translation.js';
import { ApiError } from '../../api/client.js';

/**
 * Per-language translation editor. Lists every translatable string in
 * the questionnaire (item.text + item.helpText + each answerOption
 * label) and renders a column per non-English supported language.
 * Empty cells are unfilled translations; filled cells show the
 * persisted value and let the clinician edit it inline.
 *
 * Storage: mutations go through `onUpdateItem`, which replaces the
 * item's `extension[]` array. A single translation = a single
 * `translation` extension with three sub-extensions (field, lang,
 * content). Removing the content (empty string) deletes the entry so
 * the renderer falls back to the canonical English value.
 *
 * Bead kjt acceptance #3: every translatable string with empty/filled
 * state per language.
 */

const HELP_TEXT_EXT = ExtensionUrl.helpText;
const TRANSLATION_EXT = ExtensionUrl.translation;

type Field = 'text' | 'helpText' | `answerOption:${string}`;

interface Row {
  /** linkId of the item this string belongs to. */
  itemLinkId: string;
  /** Internal field id used for the translation extension's `field` sub-ext. */
  field: Field;
  /** Human-readable label shown in the first column. */
  label: string;
  /** The canonical English source string (used as the placeholder). */
  source: string;
}

export interface TranslationsEditorProps {
  items: readonly QuestionnaireItem[];
  onUpdateItem: (linkId: string, updater: (item: QuestionnaireItem) => QuestionnaireItem) => void;
}

type BusyKey = `${string}|${Language}`;

export function TranslationsEditor({ items, onUpdateItem }: TranslationsEditorProps) {
  const rows = collectRows(items);
  const targetLanguages = SUPPORTED_LANGUAGES.filter((l) => l.code !== 'en');
  // `${rowKey}|${lang}` for an in-flight per-cell translation, plus a
  // bulk-busy entry per language column when "Translate all empty" runs.
  const [busy, setBusy] = useState<Set<BusyKey | `bulk|${Language}`>>(new Set());
  const [errorByKey, setErrorByKey] = useState<Map<string, string>>(new Map());

  if (rows.length === 0) {
    return (
      <div className="translations-editor-empty">
        <p>{'No translatable strings in this form yet — add some fields first.'}</p>
      </div>
    );
  }

  const rowKey = (r: Row) => `${r.itemLinkId}|${r.field}`;

  // Fill a single cell with the provider's draft. The clinician can still
  // edit it after; Save Form persists whatever ends up in the input.
  const translateOne = async (row: Row, lang: Language): Promise<void> => {
    const cellKey: BusyKey = `${rowKey(row)}|${lang}`;
    setBusy((b) => new Set(b).add(cellKey));
    setErrorByKey((m) => {
      if (!m.has(cellKey)) return m;
      const next = new Map(m);
      next.delete(cellKey);
      return next;
    });
    try {
      const res = await translateBatch({ sources: [row.source], targetLang: lang });
      const text = res.translations[0];
      if (typeof text === 'string') {
        onUpdateItem(row.itemLinkId, (it) => writeTranslation(it, row.field, lang, text));
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Translation failed.';
      setErrorByKey((m) => new Map(m).set(cellKey, msg));
    } finally {
      setBusy((b) => {
        const next = new Set(b);
        next.delete(cellKey);
        return next;
      });
    }
  };

  // Per-column bulk: gather every row whose target-lang cell is empty,
  // send them as one batch, then fan the results back into the items.
  const translateColumn = async (lang: Language): Promise<void> => {
    const empties = rows.filter((r) => {
      const item = items.find((i) => i.linkId === r.itemLinkId);
      return item !== undefined && readTranslation(item, r.field, lang).length === 0;
    });
    if (empties.length === 0) return;
    const bulkKey: `bulk|${Language}` = `bulk|${lang}`;
    setBusy((b) => new Set(b).add(bulkKey));
    try {
      const res = await translateBatch({
        sources: empties.map((r) => r.source),
        targetLang: lang,
      });
      empties.forEach((row, i) => {
        const text = res.translations[i];
        if (typeof text === 'string') {
          onUpdateItem(row.itemLinkId, (it) => writeTranslation(it, row.field, lang, text));
        }
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Translation failed.';
      setErrorByKey((m) => new Map(m).set(bulkKey, msg));
    } finally {
      setBusy((b) => {
        const next = new Set(b);
        next.delete(bulkKey);
        return next;
      });
    }
  };

  return (
    <div className="translations-editor" data-translations-editor>
      <div className="translations-editor-intro">
        <h2 className="translations-editor-heading">{'Translations'}</h2>
        <p className="translations-editor-subtitle">
          {
            'Click Translate to draft a translation for a cell, or "Translate all empty" to fill a whole column. Drafts are editable — review and click Save Form to persist them on the questionnaire.'
          }
        </p>
      </div>
      <table className="translations-editor-table">
        <thead>
          <tr>
            <th scope="col" className="translations-editor-col-source">
              {'English source'}
            </th>
            {targetLanguages.map((l) => {
              const bulkKey: `bulk|${Language}` = `bulk|${l.code}`;
              const bulkBusy = busy.has(bulkKey);
              const bulkError = errorByKey.get(bulkKey);
              return (
                <th key={l.code} scope="col">
                  <div className="translations-editor-col-head">
                    <span>{l.label}</span>
                    <button
                      type="button"
                      className="translations-editor-bulk-btn"
                      onClick={() => {
                        void translateColumn(l.code);
                      }}
                      disabled={bulkBusy}
                      data-translation-bulk={l.code}
                    >
                      {bulkBusy ? 'Translating…' : 'Translate all empty'}
                    </button>
                  </div>
                  {bulkError !== undefined ? (
                    <span className="translations-editor-error" role="alert">
                      {bulkError}
                    </span>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} data-translation-row={row.field}>
              <th scope="row" className="translations-editor-source-cell">
                <span className="translations-editor-source-label">{row.label}</span>
                <span className="translations-editor-source-text">{row.source}</span>
              </th>
              {targetLanguages.map((l) => {
                const item = items.find((i) => i.linkId === row.itemLinkId);
                const current = item === undefined ? '' : readTranslation(item, row.field, l.code);
                const cellKey: BusyKey = `${rowKey(row)}|${l.code}`;
                const cellBusy = busy.has(cellKey);
                const cellError = errorByKey.get(cellKey);
                return (
                  <td key={l.code} className="translations-editor-input-cell">
                    <div className="translations-editor-input-row">
                      <input
                        type="text"
                        value={current}
                        placeholder={row.source}
                        data-translation-input={`${row.field}|${l.code}`}
                        onChange={(e) => {
                          const next = e.currentTarget.value;
                          onUpdateItem(row.itemLinkId, (it) =>
                            writeTranslation(it, row.field, l.code, next),
                          );
                        }}
                      />
                      <button
                        type="button"
                        className="translations-editor-cell-btn"
                        onClick={() => {
                          void translateOne(row, l.code);
                        }}
                        disabled={cellBusy}
                        title={
                          current.length > 0 ? 'Replace with a fresh draft' : 'Draft a translation'
                        }
                      >
                        {cellBusy ? '…' : 'Translate'}
                      </button>
                    </div>
                    {cellError !== undefined ? (
                      <span className="translations-editor-error" role="alert">
                        {cellError}
                      </span>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- helpers --------------------------------------------------------------

function collectRows(items: readonly QuestionnaireItem[]): Row[] {
  const out: Row[] = [];
  for (const item of flatten(items)) {
    if (item.text !== undefined && item.text.length > 0) {
      out.push({
        itemLinkId: item.linkId,
        field: 'text',
        label: `${item.linkId} · label`,
        source: item.text,
      });
    }
    const helpText = readHelpText(item);
    if (helpText !== undefined) {
      out.push({
        itemLinkId: item.linkId,
        field: 'helpText',
        label: `${item.linkId} · help`,
        source: helpText,
      });
    }
    for (const o of item.answerOption ?? []) {
      const key = answerOptionKey(o);
      const label = answerOptionLabel(o);
      if (label.length === 0) continue;
      out.push({
        itemLinkId: item.linkId,
        field: `answerOption:${key}`,
        label: `${item.linkId} · option "${label}"`,
        source: label,
      });
    }
  }
  return out;
}

function flatten(items: readonly QuestionnaireItem[]): QuestionnaireItem[] {
  const out: QuestionnaireItem[] = [];
  for (const item of items) {
    out.push(item);
    if (item.item !== undefined) out.push(...flatten(item.item));
  }
  return out;
}

function readHelpText(item: QuestionnaireItem): string | undefined {
  const ext = item.extension?.find((e) => e.url === HELP_TEXT_EXT);
  const v = ext?.valueString;
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function readTranslation(item: QuestionnaireItem, field: Field, lang: Language): string {
  for (const e of item.extension ?? []) {
    if (e.url !== TRANSLATION_EXT) continue;
    const sub = e.extension ?? [];
    const fieldSub = sub.find((s) => s.url === 'field')?.valueCode;
    const langSub = sub.find((s) => s.url === 'lang')?.valueCode;
    if (fieldSub !== field || langSub !== lang) continue;
    const contentSub = sub.find((s) => s.url === 'content');
    return typeof contentSub?.valueString === 'string' ? contentSub.valueString : '';
  }
  return '';
}

function writeTranslation(
  item: QuestionnaireItem,
  field: Field,
  lang: Language,
  content: string,
): QuestionnaireItem {
  const others = (item.extension ?? []).filter((e) => {
    if (e.url !== TRANSLATION_EXT) return true;
    const sub = e.extension ?? [];
    const fieldSub = sub.find((s) => s.url === 'field')?.valueCode;
    const langSub = sub.find((s) => s.url === 'lang')?.valueCode;
    return !(fieldSub === field && langSub === lang);
  });
  if (content.trim().length === 0) {
    // Empty input removes the translation so the renderer falls back
    // to the English source — keeps the FHIR payload tidy.
    return { ...item, extension: others };
  }
  const next: Extension = {
    url: TRANSLATION_EXT,
    extension: [
      { url: 'field', valueCode: field },
      { url: 'lang', valueCode: lang },
      { url: 'content', valueString: content },
    ],
  };
  return { ...item, extension: [...others, next] };
}

function answerOptionKey(option: AnswerOption): string {
  if (option.valueCoding !== undefined) {
    return `c:${option.valueCoding.system ?? ''}|${option.valueCoding.code ?? ''}`;
  }
  if (option.valueString !== undefined) return `s:${option.valueString}`;
  if (option.valueInteger !== undefined) return `i:${String(option.valueInteger)}`;
  return '';
}

function answerOptionLabel(option: AnswerOption): string {
  if (option.valueCoding !== undefined) {
    return option.valueCoding.display ?? option.valueCoding.code ?? '';
  }
  if (option.valueString !== undefined) return option.valueString;
  if (option.valueInteger !== undefined) return String(option.valueInteger);
  return '';
}
